import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalOverlay,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { AdminPortalAccount } from "../../../../shared/v2/definitions/admin.types";
import { api } from "../../../../utilities/apiRequest";
import { ServiceDataSectionCard } from "../ServiceDataSectionCard";
import { ServiceAccountCreateLicenseModalContent } from "./ServiceAccountCreateLicenseModalContent";
import { ServiceAccountLicenseRow } from "./ServiceAccountLicenseRow";

type Props = {
  isFetching: boolean;
  account: AdminPortalAccount;
};

export function ServiceAccountLicenseCard({ isFetching, account }: Props) {
  const licensesQuery = api.admin.account.license.list.useQuery({
    accountID: account.accountID,
  });

  const apiUtils = api.useUtils();

  const updateLicenseMutation = api.admin.account.license.update.useMutation(
    {},
  );
  const deleteLicenseMutation = api.admin.account.license.delete.useMutation(
    {},
  );
  const createLicenseMutation = api.admin.account.license.create.useMutation(
    {},
  );

  const isMutating =
    updateLicenseMutation.isPending ||
    deleteLicenseMutation.isPending ||
    createLicenseMutation.isPending;

  const { isLoading } = licensesQuery;

  const handleUpdateLicense = async (
    licenseLineID: string,
    quantity: number,
  ) => {
    await updateLicenseMutation.mutateAsync({
      licenseDTO: {
        licenseLineID,
        quantity,
      },
    });
    apiUtils.admin.account.license.invalidate();
    apiUtils.admin.history.invalidate();
  };
  const handleDeleteLicense = async (licenseLineID: string) => {
    await deleteLicenseMutation.mutateAsync({
      licenseLineID,
    });
    apiUtils.admin.account.license.invalidate();
    apiUtils.admin.history.invalidate();
  };
  const handleCreateLicense = async (code: string, quantity: number) => {
    await createLicenseMutation.mutateAsync({
      licenseDTO: {
        accountID: account.accountID,
        code,
        quantity,
      },
    });
    apiUtils.admin.account.license.invalidate();
    apiUtils.admin.history.invalidate();
  };

  const createLicenseModalState = useDisclosure();

  const sortedLicenses =
    licensesQuery.data?.licenses.sort((a, b) => {
      if (a.detail.type === "core" && b.detail.type !== "core") {
        return -1;
      }
      if (a.detail.type !== "core" && b.detail.type === "core") {
        return 1;
      }
      return a.detail.internalName.localeCompare(b.detail.internalName);
    }) || [];

  return (
    <ServiceDataSectionCard
      title="License"
      actions={
        account.accountStatus !== "deleted" && (
          <Button
            onClick={createLicenseModalState.onOpen}
            isDisabled={isFetching}
            isLoading={createLicenseMutation.isPending}
            loadingText="Adding..."
            size="sm">
            Add license
          </Button>
        )
      }>
      <VStack w="100%" spacing="2" align="start">
        {licensesQuery.isSuccess ? (
          <>
            <Modal
              scrollBehavior="inside"
              size="full"
              isOpen={createLicenseModalState.isOpen}
              onClose={createLicenseModalState.onClose}>
              <ModalOverlay />
              <ServiceAccountCreateLicenseModalContent
                accountLicenses={licensesQuery.data.licenses}
                onClose={createLicenseModalState.onClose}
                onConfirm={handleCreateLicense}
              />
            </Modal>
            <Badge
              colorScheme={
                account.license.licenseStatus === "ok" ? "brand" : "red"
              }>
              {account.license.licenseStatus}
            </Badge>
            <Table variant="data-table" size="sm">
              <Thead>
                <Tr>
                  <Th>License</Th>
                  <Th>Quantity</Th>
                  <Th>Description</Th>
                  <Th>Display</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {sortedLicenses.map((row) => (
                  <ServiceAccountLicenseRow
                    isLoading={isLoading || isMutating}
                    row={row}
                    key={row.licenseLineID}
                    onDeleteLicense={handleDeleteLicense}
                    onUpdateLicense={handleUpdateLicense}
                  />
                ))}
              </Tbody>
            </Table>
            <Heading size="xs">Entitlements</Heading>
            <Flex gap="2" flexWrap="wrap">
              {Object.entries(licensesQuery.data.calculatedEntitlements).map(
                ([key, value]) => (
                  <Box
                    key={key}
                    borderWidth="1px"
                    borderRadius="md"
                    borderStyle="solid"
                    p="2">
                    <Text fontWeight="bold">{key}</Text>
                    <Text>{value}</Text>
                  </Box>
                ),
              )}
            </Flex>
          </>
        ) : licensesQuery.isError ? (
          <Text color="red.500">Error: {licensesQuery.error.message}</Text>
        ) : (
          <Text>Loading...</Text>
        )}
      </VStack>
    </ServiceDataSectionCard>
  );
}
